import { cloneDeep, toInteger } from 'lodash';
import { Framework, type IFramework } from '@/framework';
import type { IHistoryDefaultData } from '@/framework/services/history/type';
import { Layer, type LayerInsertPosition } from '@/layers/layer';
import { limitMaxImageInfo, loadImageAsync } from '@/utils/file-kit';
import {
    LayerType,
    PluginType,
    type IAgentInfo,
    type IExportPlugin,
    type ILayerProps,
    type IMediaInfo,
    type IMediaLayerProps,
} from '@/version-types';
import mediaLayerBehavior from './behavior';
import { getMediaLayerProps } from './constants/default-media-data';
import { type IEdgelessMediaLayer } from './edgeless';
import { loadVideoMetaInfo, resolveVideoPosterUrl } from './utils/video';

/**
 *  重新加载 edgeless 的缓存资源（video 元素和封面在画布对象中有缓存）
 *
 * @param layerId - 图层 ID
 */
async function reloadEdgelessResource(framework: IFramework, layerId: string) {
    const edgelessObj = framework.service.edgelessCanvas?.getByLayerId?.(layerId) as unknown as IEdgelessMediaLayer;
    if (edgelessObj && typeof edgelessObj.reloadResource === 'function') {
        await edgelessObj.reloadResource();
    }
}

class MediaLayerHistoryListener extends Framework {
    constructor(identity: string) {
        super(identity);

        this.setupHistoryListener();
    }

    /**
     * 注册历史记录 undo/redo 监听
     * 当 undo/redo 涉及视频图层且 URL 发生变化时，自动 reload edgeless 缓存资源
     */
    private setupHistoryListener() {
        this.framework.service.history.onChange(async (args, historyItem) => {
            // 只关心 redo/undo 操作
            if (!args.isRedo && !args.isUndo) return;
            if (args.isCustom || !historyItem) return;

            const data = historyItem.data as IHistoryDefaultData;

            // 单图层情况
            if (data.newLayer && data.oldLayer) {
                await this.reloadMediaLayerIfNeeded(data.oldLayer, data.newLayer);
            }
        });
    }

    /**
     * 判断 undo/redo 的图层是否是媒体图层且资源发生了变化，如果是则重新加载 edgeless 资源
     */
    private async reloadMediaLayerIfNeeded(oldLayer: ILayerProps, newLayer: ILayerProps) {
        // 只处理媒体图层
        if (newLayer.layerType !== LayerType.MEDIA_LAYER) return;

        const oldData = oldLayer.data as IMediaLayerProps;
        const newData = newLayer.data as IMediaLayerProps;

        // 检查 URL 或封面是否发生了变化
        const urlChanged = oldData?.url !== newData?.url;
        const posterChanged = oldData?.poster !== newData?.poster;

        if (!urlChanged && !posterChanged) return;

        // redo 时使用 newLayer 的 id，undo 时也是同一个图层 id
        const layerId = newLayer.id;
        await reloadEdgelessResource(this.framework, layerId);
    }
}

export class MediaLayer extends Layer {
    /** 更新媒体图层并设置为激活状态 */
    updateLayer(newLayer: ILayerProps, oldLayer?: ILayerProps, pushHistory = true, trackFunc?: string) {
        this.framework.service.frame.addOrUpdateLayer(newLayer);
        if (pushHistory && oldLayer) {
            this.framework.service.history.write(cloneDeep(oldLayer), newLayer, trackFunc);
        }
        this.framework.service.frame.setActiveLayer(newLayer.id);
    }

    /**
     * 替换媒体图层的视频 URL 并更新相关属性
     * 自动加载新视频获取尺寸，更新图层数据，写入历史，刷新 edgeless 渲染缓存
     *
     * @param layerId - 图层 ID
     * @param newUrl - 新的视频 URL
     * @param coverUrl - 可选；新的封面 URL
     * @param funcUseList - 可选；新的功能使用列表
     * @param autoGeneratePoster - 是否自动生成首帧封面，默认 true
     * @param extraInfoPatch - 可选；与视频地址一起提交的图层额外信息
     * @param resizeLayerToVideoRatio - 可选；是否按新视频比例更新画布图层尺寸
     * @returns 更新完成后结束；图层不存在时直接结束
     */
    async replaceUrl({
        layerId,
        newUrl,
        coverUrl,
        funcUseList,
        autoGeneratePoster = true,
        extraInfoPatch,
        resizeLayerToVideoRatio = false,
    }: {
        layerId: string;
        newUrl: string;
        coverUrl?: string;
        funcUseList?: string[];
        autoGeneratePoster?: boolean;
        extraInfoPatch?: Record<string, unknown>;
        resizeLayerToVideoRatio?: boolean;
    }) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer) return;

        const oldLayer = cloneDeep(layer);
        const data = layer.data as IMediaLayerProps;

        let naturalWidth = data.naturalWidth;
        let naturalHeight = data.naturalHeight;
        let duration = data.duration;
        const videoMetaInfoPromise = loadVideoMetaInfo(newUrl).catch((error) => {
            console.error('Failed to load video metadata:', error);
            return undefined;
        });
        const [mediaInfo, posterUrl] = await Promise.all([
            videoMetaInfoPromise,
            resolveVideoPosterUrl(newUrl, coverUrl, autoGeneratePoster),
        ]);
        if (mediaInfo) {
            naturalWidth = mediaInfo.width;
            naturalHeight = mediaInfo.height;
            duration = mediaInfo.duration;
        }

        // 更新视频 URL 和视频元信息
        data.url = newUrl;
        if (posterUrl !== undefined) {
            data.poster = posterUrl;
        }

        data.naturalWidth = naturalWidth;
        data.naturalHeight = naturalHeight;
        data.duration = duration;

        if (resizeLayerToVideoRatio && naturalWidth > 0 && naturalHeight > 0 && layer.width > 0) {
            const nextHeight = Math.max(1, toInteger((layer.width * naturalHeight) / naturalWidth));
            // 保持图层中心不变，避免导出比例变化后视频在画布上发生位置跳动。
            layer.top += (layer.height - nextHeight) / 2;
            layer.height = nextHeight;
        }

        layer.extraInfo = {
            ...layer.extraInfo,
            ...extraInfoPatch,
            ...(funcUseList !== undefined ? { funcUseList } : {}),
        };

        // 更新图层数据和历史
        this.updateLayer(layer, oldLayer, true);

        await reloadEdgelessResource(this.framework, layerId);
    }

    /**
     * 设置视频链接地址
     *
     * @param id - 图层 ID
     * @param data - 数据；`data.poster` 封面 URL
     */
    async setPosterUrl(id: string, { url }: { url: string }) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        if (!layer) return;
        const data = layer.data as IMediaLayerProps;
        data.poster = url;
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.history.write(layer);
        this.framework.service.frame.setActiveLayer(id);
    }

    /**
     * 添加媒体图层
     *
     * @param materialId - 图层素材 ID
     * @param url - 媒体链接地址
     * @param info - 媒体信息
     * @param byMousePosition - 是否读取当前鼠标位置
     * @param agent - 可选的 Agent 图层信息
     * @param position - 可选的固定插入位置，优先于鼠标位置
     * @returns 新增的媒体图层
     */
    async addMediaLayer(
        materialId: string,
        url: string,
        info: IMediaInfo,
        byMousePosition = false,
        agent?: IAgentInfo,
        position?: LayerInsertPosition,
    ) {
        let { naturalWidth, naturalHeight } = info;
        const { width: stageWidth, height: stageHeight } = this.framework.service.frame.getActiveFrameSize();

        const stageRatio = stageWidth / stageHeight;
        if (!naturalWidth || !naturalHeight) {
            const { width, height } = await loadImageAsync(url);
            naturalWidth = width;
            naturalHeight = height;
        }

        // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
        const limitResult = limitMaxImageInfo(stageWidth, url, naturalWidth, naturalHeight);
        naturalWidth = limitResult.naturalWidth;
        naturalHeight = limitResult.naturalHeight;
        url = limitResult.url;

        const ratio = naturalWidth / naturalHeight;

        let width = 0;
        let height = 0;
        if (stageRatio > ratio) {
            height = toInteger(stageHeight / 2);
            width = toInteger(naturalWidth / (naturalHeight / height));
        } else {
            width = toInteger(stageWidth / 2);
            height = toInteger(naturalHeight / (naturalWidth / width));
        }

        const layer = getMediaLayerProps(
            {
                url,
                width,
                height,
                materialId,
            },
            info,
        );

        if (agent) {
            layer.extraInfo = {
                ...layer.extraInfo,
                agent,
            };
        }

        if (position) return this.addByInsertPosition(layer, position);
        if (byMousePosition) return this.addByMousePosition(layer);
        return this.addByDiagonal(layer);
    }

    /**
     * 替换图片为媒体图形
     *
     * @param layerId - 图层 ID
     * @param materialId - 图层素材 ID
     * @param mediaInfo - 媒体图形信息
     */
    replaceImg2Media(layerId: string, materialId: string, mediaInfo: any) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!oldLayer) {
            return;
        }
        const { top, left, parentId } = oldLayer;
        const { width: stageWidth, height: stageHeight } = this.framework.service.frame.getActiveFrameSize();
        const { info, url } = mediaInfo;
        const { naturalWidth, naturalHeight } = info;

        const stageRatio = stageWidth / stageHeight;
        const ratio = naturalWidth / naturalHeight;
        let width = 0;
        let height = 0;
        if (stageRatio > ratio) {
            height = toInteger(stageHeight / 2);
            width = toInteger(naturalWidth / (naturalHeight / height));
        } else {
            width = toInteger(stageWidth / 2);
            height = toInteger(naturalHeight / (naturalWidth / width));
        }

        const layer = getMediaLayerProps(
            {
                url,
                left,
                top,
                width,
                height,
                materialId,
            },
            info,
        );

        layer.id = layerId;
        layer.parentId = parentId;
        this.framework.service.frame.addOrUpdateLayer(cloneDeep(layer));
        this.framework.service.history.write(cloneDeep(oldLayer));
        this.framework.service.frame.setActiveLayer(layer.id);
    }

    protected override getAgentCreateOptions() {
        return {
            ...super.getAgentCreateOptions(),
            disableResize: true,
        };
    }
}

/**
 * 媒体图层插件
 */
export const mediaLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.MEDIA_LAYER,
    createInstance({ identity }) {
        return [
            { key: 'MediaLayer', value: new MediaLayer(identity) },
            { key: 'MediaLayerHistoryListener', value: new MediaLayerHistoryListener(identity) },
        ];
    },
    createBehavior() {
        return mediaLayerBehavior;
    },
};

export { EdgelessMediaLayer, type IEdgelessMediaLayer } from './edgeless';
